import React from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Link as RouterLink } from 'react-router-dom';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Tooltip from '@material-ui/core/Tooltip';
import { BackstageTheme } from '@backstage/theme';
import { useLocation } from 'react-router-dom';

const useStyles = makeStyles((theme: BackstageTheme) => ({
  listItem: {
    paddingLeft: theme.spacing(2),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      borderLeft: '4px solid #FFDD00',
      paddingLeft: theme.spacing(1.5),
    },
    '&.Mui-selected': {
      backgroundColor: 'transparent',
      borderLeft: '4px solid #FFDD00',
      paddingLeft: theme.spacing(1.5),
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
    transition: theme.transitions.create(['background-color', 'padding', 'width'], {
      duration: theme.transitions.duration.shorter,
    }),
  },
  listItemCollapsed: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    justifyContent: 'center',
    overflow: 'hidden',
    '&:hover': {
      borderLeft: '4px solid #FFDD00',
      paddingLeft: theme.spacing(1.5),
    },
    '&.Mui-selected': {
      backgroundColor: 'transparent',
      borderLeft: '4px solid #FFDD00',
      paddingLeft: theme.spacing(1.5),
    },
  },
  listItemText: {
    transition: theme.transitions.create('opacity', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  tooltip: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
    fontSize: '0.875rem',
    padding: theme.spacing(1, 2),
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    borderRadius: '0 4px 4px 0',
    marginLeft: '0 !important',
  },
  tooltipArrow: {
    color: theme.palette.background.paper,
  },
  tooltipPopper: {
    zIndex: 1500,
  },
}));

interface PichinchaMenuItemProps {
  text: string;
  icon: React.ReactNode;
  path: string;
  isCollapsed?: boolean;
}

/**
 * Componente PichinchaMenuItem que encapsula los elementos del menú con los estilos corporativos
 */
export const PichinchaMenuItem: React.FC<PichinchaMenuItemProps> = ({
  text,
  icon,
  path,
  isCollapsed = false,
}) => {
  const classes = useStyles();
  const theme = useTheme<BackstageTheme>();
  const location = useLocation();
  const isSelected = location.pathname === path || location.pathname.startsWith(`${path}/`);

  if (isCollapsed) {
    return (
      <Tooltip
        title={text}
        placement="right"
        arrow
        classes={{
          tooltip: classes.tooltip,
          arrow: classes.tooltipArrow,
          popper: classes.tooltipPopper
        }}
        PopperProps={{
          style: {
            zIndex: 3000
          }
        }}
      >
        <ListItem 
          button 
          component={RouterLink} 
          to={path}
          className={`${classes.listItem} ${classes.listItemCollapsed}`}
          selected={isSelected}
          aria-label={text}
          tabIndex={0}
          style={{
            transition: 'all 0.2s ease-in-out',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 'auto'
          }}
        >
          <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'center', 
            minWidth: '24px',
            opacity: 1,
            zIndex: 10
          }}>
            <div style={{ fontSize: '24px' }}>
              {icon}
            </div>
          </div>
        </ListItem>
      </Tooltip>
    );
  }

  return (
    <ListItem 
      button 
      component={RouterLink} 
      to={path}
      className={classes.listItem}
      selected={isSelected}
      aria-label={text}
      tabIndex={0}
      style={{
        transition: 'all 0.2s ease-in-out',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        width: 'auto'
      }}
    >
      <div style={{ 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'center', 
        minWidth: '36px',
        marginRight: theme.spacing(1),
        opacity: 1,
        zIndex: 10
      }}>
        {icon}
      </div>
      
      <ListItemText 
        primary={text} 
        className={classes.listItemText}
      />
    </ListItem>
  );
};
